import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { isLabelGroundTruth, type MeasurementSource, type StoredReport } from '@wrp/shared';
import * as api from '../api';
import { useDialogs } from '../components/Dialogs';
import { Icon, IconButton } from '../components/Icon';
import { RssiBar } from '../components/RssiBar';
import { formatDateTime, shortId, strongestRssi } from '../format';
import { useApp, useSafeAction, useStreamEvent } from '../store';
import { useRecordedReportIds } from './shared';

const PAGE_SIZE = 50;

/** Floor a report was surveyed on, by name once that floor is known, else its raw id. */
function floorText(report: StoredReport, names: Map<string, string>): string {
  const id = report.ground_truth?.floor_id;
  if (!id) return '—';
  return names.get(id) ?? id;
}

/** Reference-point label a report was tagged with, for the label form of ground truth. */
function labelText(report: StoredReport): string {
  const gt = report.ground_truth;
  return gt && isLabelGroundTruth(gt) ? gt.label : '—';
}

/** Coordinates a report was tagged with, for the coordinate form of ground truth. */
function positionText(report: StoredReport): string {
  const gt = report.ground_truth;
  return gt && !isLabelGroundTruth(gt) ? `${gt.x.toFixed(2)}, ${gt.y.toFixed(2)} m` : '—';
}

/**
 * Every stored report, newest first, with its AP detail and delete actions. Unlike the other Live
 * modes this one is not floor-scoped - a report only has a floor when it carries ground truth, so
 * filtering by the selected floor would hide most ordinary scans.
 */
export function ReportsMode() {
  const { floors, refreshFingerprints, pushToast } = useApp();
  const dialogs = useDialogs();
  const safe = useSafeAction();
  const recorded = useRecordedReportIds();

  const [items, setItems] = useState<StoredReport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deviceFilter, setDeviceFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<MeasurementSource | ''>('');

  const filters = useMemo(
    () => ({ device_id: deviceFilter.trim() || undefined, source: sourceFilter || undefined }),
    [deviceFilter, sourceFilter],
  );
  const floorNames = useMemo(() => new Map(floors.map((f) => [f.id, f.name])), [floors]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await safe('Failed to load reports', () =>
      api.listReports({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    );
    if (res) {
      setItems(res.items);
      setTotal(res.total);
    }
    setLoading(false);
  }, [safe, filters, page]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live arrivals only belong at the top of the first page, and only when they pass the filters.
  useStreamEvent<StoredReport>('report', (report) => {
    if (page !== 0) return;
    if (filters.device_id && report.device_id !== filters.device_id) return;
    if (filters.source && report.source !== filters.source) return;
    // A labeled report supersedes the previous one at that reference point, so the list has to be
    // refetched rather than just extended - one of the rows on screen is gone.
    if (report.ground_truth && isLabelGroundTruth(report.ground_truth)) {
      void load();
      return;
    }
    setItems((prev) => [report, ...prev.filter((r) => r.id !== report.id)].slice(0, PAGE_SIZE));
    setTotal((prev) => prev + 1);
  });

  // `fingerprint_id` on a streamed report is set at ingest time and can go stale, so fall back to
  // the fingerprint store, which the SSE hub keeps current.
  const isFingerprint = (report: StoredReport) =>
    report.fingerprint_id != null || recorded.has(report.id);

  const deleteOne = async (report: StoredReport) => {
    const ok = await dialogs.confirm(
      isFingerprint(report)
        ? 'Delete this report? It backs a fingerprint, which is deleted with it - its reference point may go back to pending.'
        : `Delete the report from ${report.device_id || 'unknown device'} at ${formatDateTime(report.received_at)}?`,
    );
    if (!ok) return;
    const done = await safe('Failed to delete report', async () => {
      await api.deleteReport(report.id);
      return true;
    });
    if (!done) return;
    if (expandedId === report.id) setExpandedId(null);
    await Promise.all([load(), refreshFingerprints()]);
  };

  const deleteAll = async () => {
    const filtered = filters.device_id !== undefined || filters.source !== undefined;
    const ok = await dialogs.confirm(
      `Delete ${filtered ? 'all matching reports' : `all ${total} reports`}? Fingerprints built from them ` +
        'are deleted too, so completed reference points may go back to pending. Reference points, APs ' +
        'and floors are kept.',
    );
    if (!ok) return;
    const res = await safe('Failed to delete reports', () => api.deleteReports(filters));
    if (!res) return;
    setExpandedId(null);
    setPage(0);
    await Promise.all([load(), refreshFingerprints()]);
    pushToast('success', `Deleted ${res.deleted} report${res.deleted === 1 ? '' : 's'}`);
  };

  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE + items.length);
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="live__full">
      <section className="box">
        <header className="box__head">
          <Icon name="list" />
          <h3>Reports</h3>
          <span className="box__count">{total === 0 ? 'none' : `${from}–${to} of ${total}`}</span>
          <span className="box__spacer" />
          <input
            className="box__filter"
            type="text"
            placeholder="device id"
            value={deviceFilter}
            aria-label="Filter by device id"
            onChange={(e) => {
              setDeviceFilter(e.target.value);
              setPage(0);
            }}
          />
          <select
            className="box__filter"
            value={sourceFilter}
            aria-label="Filter by source"
            onChange={(e) => {
              setSourceFilter(e.target.value as MeasurementSource | '');
              setPage(0);
            }}
          >
            <option value="">any source</option>
            <option value="scan">scan</option>
            <option value="beacon_report_11k">beacon_report_11k</option>
          </select>
          <IconButton icon="radar" label="Reload" size={14} onClick={() => void load()} />
          <IconButton
            icon="broom"
            label="Delete all reports"
            size={14}
            danger
            disabled={total === 0}
            onClick={() => void deleteAll()}
          />
        </header>

        <div className="box__list">
          {items.length === 0 ? (
            <p className="box__empty">
              {loading ? 'Loading…' : 'No reports. Send a scan from the device or run the simulator.'}
            </p>
          ) : (
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Device</th>
                  <th>Source</th>
                  <th>APs</th>
                  <th>Strongest</th>
                  <th>Floor</th>
                  <th>Label</th>
                  <th>Position</th>
                  <th>Fingerprint</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((report) => {
                  const expanded = report.id === expandedId;
                  const best = strongestRssi(report);
                  return (
                    <Fragment key={report.id}>
                      <tr
                        className={expanded ? 'is-active' : undefined}
                        title={`Report ${shortId(report.id)} — click for the AP list`}
                        onClick={() => setExpandedId(expanded ? null : report.id)}
                      >
                        <td>{formatDateTime(report.received_at)}</td>
                        <td>{report.device_id || '—'}</td>
                        <td>
                          <span className="badge badge--scan">{report.source}</span>
                        </td>
                        <td>{report.aps.length}</td>
                        <td>{best === null ? '—' : `${best} dBm`}</td>
                        <td title={report.ground_truth?.floor_id}>{floorText(report, floorNames)}</td>
                        <td>{labelText(report)}</td>
                        <td>{positionText(report)}</td>
                        <td>{isFingerprint(report) ? <span className="badge badge--fp">yes</span> : '—'}</td>
                        <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            icon="trash"
                            label="Delete this report"
                            danger
                            size={14}
                            onClick={() => void deleteOne(report)}
                          />
                        </td>
                      </tr>
                      {expanded ? (
                        <tr className="row-detail">
                          <td colSpan={10}>
                            <ul className="box__list box__list--inline">
                              {report.aps.length === 0 ? (
                                <li className="box__empty">This report carries no APs.</li>
                              ) : null}
                              {report.aps.map((ap) => (
                                <li key={ap.bssid} className="row row--scan">
                                  <span className="row__main">
                                    <strong>{ap.ssid || ap.bssid}</strong>
                                    <span className="muted">
                                      {ap.bssid}
                                      {ap.channel ? ` · ch ${ap.channel}` : ''}
                                    </span>
                                  </span>
                                  {typeof ap.signal_strength === 'number' ? (
                                    <RssiBar dbm={ap.signal_strength} />
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {total > PAGE_SIZE ? (
          <div className="box__body pager">
            <button
              type="button"
              className="btn"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <span className="muted">
              page {page + 1} of {lastPage + 1}
            </span>
            <button
              type="button"
              className="btn"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
